import React, { useState } from 'react';
import {
  Button,
  Typography,
  Stack,
  DialogContent,
  Box,
  IconButton,
  TextField,
  Divider,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { Add as AddIcon, Close, Delete as DeleteIcon } from '@mui/icons-material';
import DialogHeader from '@shared/ui/dialog/DialogHeader';
import { CreateSupplierAddressRequestDTO } from '@infrastructure/suppliers/dto/SupplierDTO';
import { useSuppliers } from '../../hooks/useSuppliers';
import { useToast } from '@shared/hooks';
import AddDraftAddressDialog from '../address/AddDraftAddressDialog';
import ProductSelectBottomSheet from '@features/products/components/selection/ProductSelectBottomSheet';
import LinkProductsToSupplier from '../links/LinkProductsToSupplier';
import { Supplier } from '@domain/suppliers/models/Supplier';
import { Product } from '@domain/product/models/Product';

interface CreateSupplierBottomSheetProps {
  handleClose: () => void;
}

interface SupplierFormData {
  name: string;
  email: string;
  phone: string;
  website: string;
  isActive: boolean;
}

type CreateSupplierStep = 'details' | 'select-products' | 'configure-products';

const CreateSupplierBottomSheet: React.FC<CreateSupplierBottomSheetProps> = ({
  handleClose: handleCloseBottomSheet,
}) => {
  const { success, error: showError } = useToast();
  const { createSupplier, isCreating } = useSuppliers();
  const [step, setStep] = useState<CreateSupplierStep>('details');
  const [createdSupplier, setCreatedSupplier] = useState<Supplier | null>(null);
  const [selectedProducts, setSelectedProducts] = useState<Product[]>([]);
  const [formData, setFormData] = useState<SupplierFormData>({
    name: '',
    email: '',
    phone: '',
    website: '',
    isActive: true,
  });
  const [addresses, setAddresses] = useState<CreateSupplierAddressRequestDTO[]>([]);
  const [addressDialogOpen, setAddressDialogOpen] = useState(false);

  const handleClose = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      website: '',
      isActive: true,
    });
    setAddresses([]);
    setStep('details');
    setCreatedSupplier(null);
    setSelectedProducts([]);
    handleCloseBottomSheet();
  };

  const handleInputChange = (field: keyof SupplierFormData, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleContinue = async () => {
    if (!formData.name.trim()) {
      return;
    }

    try {
      const emailTrim = formData.email?.trim();
      const websiteTrim = formData.website?.trim();
      const supplier = await createSupplier({
        name: formData.name.trim(),
        ...(emailTrim ? { email: emailTrim } : {}),
        phone: formData.phone,
        ...(websiteTrim ? { website: websiteTrim } : {}),
        isActive: formData.isActive,
        addresses,
      });

      setCreatedSupplier(supplier);
      setStep('select-products');
      success('Supplier created successfully');
    } catch {
      showError('Failed to create supplier');
    }
  };

  const isFormValid = Boolean(formData.name.trim());

  const handleDraftAddressSave = (address: CreateSupplierAddressRequestDTO) => {
    setAddresses(prev => [...prev, address]);
  };

  const removeAddressAt = (index: number) => {
    setAddresses(prev => prev.filter((_, i) => i !== index));
  };

  if (step === 'configure-products' && createdSupplier && selectedProducts.length > 0) {
    return (
      <LinkProductsToSupplier
        initialSupplier={createdSupplier}
        initialProducts={selectedProducts}
        onClose={handleClose}
      />
    );
  }

  if (step === 'select-products' && createdSupplier) {
    return (
      <ProductSelectBottomSheet
        title="Link products"
        allowEmptyConfirm
        confirmButtonText={(count) =>
          count === 0
            ? 'Skip'
            : `Continue with ${count} product${count !== 1 ? 's' : ''}`
        }
        onFinish={(selections) => {
          if (selections.length === 0) {
            handleClose();
            return;
          }
          setSelectedProducts(selections.map((selection) => selection.product));
          setStep('configure-products');
        }}
        onClose={handleClose}
      />
    );
  }

  return (
    <>
      <DialogHeader>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" component="div">
            Create Supplier
          </Typography>
          <IconButton onClick={handleClose}>
            <Close />
          </IconButton>
        </Stack>
      </DialogHeader>

      <DialogContent sx={{ flex: 1, overflow: 'auto' }}>
        <Stack gap={3}>
          <Box>
            <Typography variant="h6" color="text.primary" gutterBottom>
              Basic information
            </Typography>
            <Stack gap={2}>
              <TextField
                fullWidth
                label="Supplier name"
                value={formData.name}
                onChange={e => handleInputChange('name', e.target.value)}
                placeholder="e.g., TechCorp Electronics"
                variant="outlined"
                required
              />
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={e => handleInputChange('email', e.target.value)}
                placeholder="orders@supplier.com"
                variant="outlined"
              />
              <TextField
                fullWidth
                label="Phone"
                value={formData.phone}
                onChange={e => handleInputChange('phone', e.target.value)}
                placeholder="+1 (555) 123-4567"
                variant="outlined"
              />
              <TextField
                fullWidth
                label="Website"
                type="url"
                value={formData.website}
                onChange={e => handleInputChange('website', e.target.value)}
                placeholder="https://supplier.com"
                variant="outlined"
              />
            </Stack>
          </Box>

          <Divider />

          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="h6" color="text.primary">
                Addresses
              </Typography>
              <Button
                variant="text"
                color="primary"
                endIcon={<AddIcon />}
                onClick={() => setAddressDialogOpen(true)}
              >
                Add address
              </Button>
            </Stack>
            {addresses.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No addresses yet. Add one if you want to include it when creating this supplier.
              </Typography>
            ) : (
              <List disablePadding dense>
                {addresses.map((addr, index) => (
                  <ListItem
                    key={`${addr.name}-${index}`}
                    secondaryAction={
                      <IconButton edge="end" aria-label="Remove address" onClick={() => removeAddressAt(index)} size="small">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    }
                    sx={{ px: 0, alignItems: 'flex-start', borderBottom: 1, borderColor: 'divider', py: 1 }}
                  >
                    <ListItemText
                      primary={addr.name}
                      secondary={`${addr.address}, ${addr.city}${addr.state ? `, ${addr.state}` : ''} ${addr.zipCode}`.trim()}
                      primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        </Stack>
      </DialogContent>

      <Box
        sx={{
          p: 2,
          borderTop: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Stack direction="row" justifyContent="flex-end" spacing={2}>
          <Button variant="outlined" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => void handleContinue()}
            disabled={!isFormValid || isCreating}
          >
            {isCreating ? 'Creating…' : 'Continue'}
          </Button>
        </Stack>
      </Box>

      <AddDraftAddressDialog
        open={addressDialogOpen}
        onClose={() => setAddressDialogOpen(false)}
        onSave={handleDraftAddressSave}
      />
    </>
  );
};

export default CreateSupplierBottomSheet;
